import {useCachePreheatQueries, useCachePreheatQuery} from '@github-ui/relay-query-cache/hooks/use-cache-preheat-query'
import {useMemo} from 'react'

import {IssueViewerViewGraphqlQuery} from '../components/IssueViewer'
import {IssueViewerSecondaryGraphqlQuery} from '../components/IssueViewerSecondaryView'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  source: string
  allowedOwner?: string
  tabbedTimeline?: boolean
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
  }>
  source: string
  tabbedTimeline?: boolean
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  source,
  allowedOwner,
  tabbedTimeline,
}: UseCachePreheatIssueViewerProps) {
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  useCachePreheatQuery({
    query: IssueViewerViewGraphqlQuery,
    variables: {
      owner,
      repo,
      number,
      count: 15,
      allowedOwner,
      tabbedTimeline: tabbedTimeline ?? false,
      ...(tabbedTimeline ? {viewType: 'CONVERSATION'} : {}),
    },
    source,
    excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
    highPriority: true,
  })

  useCachePreheatQuery({
    query: IssueViewerSecondaryGraphqlQuery,
    variables: {
      owner,
      repo,
      number,
      markAsRead: false,
    },
    source,
    excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
  })
}

export function useCachePreheatIssueViewerQueries({
  issues,
  source,
  tabbedTimeline,
}: UseCachePreheatIssueViewerQueriesProps) {
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const queries = useMemo(() => {
    const primaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerViewGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        count: 15,
        tabbedTimeline: tabbedTimeline ?? false,
        ...(tabbedTimeline ? {viewType: 'CONVERSATION'} : {}),
      },
      source,
      excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
      highPriority: true,
    }))

    const secondaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerSecondaryGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        markAsRead: false,
      },
      source,
      excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
    }))

    return [...primaryQueries, ...secondaryQueries]
  }, [issues, source, tabbedTimeline, excludeFromPrimaryQueryCacheKey, excludeFromSecondaryQueryCacheKey])

  useCachePreheatQueries({queries})
}
